import { useServerFn } from "@tanstack/react-start";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  getGrowthSettings,
  getPlanContent,
  updateGrowthSetting,
  updatePlanContent,
} from "@/lib/growth-plan.functions";

export const Route = createFileRoute("/_authenticated/admin/growth-plan/settings")({
  component: SettingsPage,
});

const inputCls =
  "w-full border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";

const GROUPS: Record<string, string> = {
  structure:
    "KCN team structure (taken off the top of GCI). Example: GCI = $10k, KCN takes $2k.",
  brokerage:
    "Brokerage / market center structure (applied to the agent's portion after the KCN split). Example: of the remaining $8k, the agent pays their KWAZ split.",
  replacement: "Replacement costs KCN covers, annual",
  growth: "Growth lift scenarios",
};


function SettingsPage() {
  const listFn = useServerFn(getGrowthSettings);
  const saveFn = useServerFn(updateGrowthSetting);
  const contentFn = useServerFn(getPlanContent);
  const saveContentFn = useServerFn(updatePlanContent);

  const [rows, setRows] = useState<any[]>([]);
  const [content, setContent] = useState<any[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setRows((await listFn({})) as any[]);
        setContent((await contentFn({})) as any[]);
      } catch (e: any) {
        setStatus(e.message);
      }
    })();
  }, [listFn, contentFn]);

  async function saveAll() {
    setBusy(true);
    setStatus(null);
    try {
      for (const r of rows) await saveFn({ data: { key: r.key, value: Number(r.value) || 0 } });
      for (const c of content) await saveContentFn({ data: { section: c.section, heading: c.heading, body: c.body } });
      setStatus("Saved.");
    } catch (e: any) {
      setStatus(e.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const groups = Array.from(new Set(rows.map((r) => r.group_name)));
  const otherGroups = groups.filter((g) => g !== "structure" && g !== "brokerage");

  function renderRow(r: any) {
    return (
      <div key={r.key} className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
        <span className="min-w-0 flex-1 text-[0.95rem] leading-snug text-foreground/85">{r.label}</span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-foreground/50">{r.unit === "percent" ? "%" : "$"}</span>
          <input
            type="number"
            step={r.unit === "percent" ? 0.5 : 50}
            min={0}
            className="w-28 border border-border bg-background px-3 py-2 text-right text-sm tabular-nums text-foreground focus:border-primary focus:outline-none"
            value={r.value}
            onChange={(e) => setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, value: e.target.value } : x)))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <p className="mb-6 text-sm text-foreground/65">
        Every number in the model lives here. A cap of 0 means no cap is applied. The tool models SOI business, where
        the agent keeps 80 percent on the KCN team side.
      </p>

      <div className="mb-10 grid gap-6 md:grid-cols-2">
        <div>
          <div className="mb-3 text-xs uppercase tracking-[0.18em] text-foreground/60">KCN team splits</div>
          <div className="border border-border">
            {rows.filter((r) => r.group_name === "structure").map(renderRow)}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground/55">
            KCN team splits are separate from brokerage fees and are taken off the top of GCI. SOI business is 80
            percent to the agent. New business (50 percent) is not modeled here, since new lead volume cannot be
            reliably projected.
          </p>
        </div>

        <div>
          <div className="mb-3 text-xs uppercase tracking-[0.18em] text-foreground/60">KW brokerage fees</div>
          <div className="border border-border">
            {rows.filter((r) => r.group_name === "brokerage").map(renderRow)}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground/55">
            Applied to the agent's portion after the KCN split. After each cap is met, that split drops to zero until
            the anniversary date.
          </p>
        </div>
      </div>

      {otherGroups.map((g) => (
        <div key={g} className="mb-8">
          <div className="mb-3 text-xs uppercase tracking-[0.18em] text-foreground/60">{GROUPS[g] ?? g}</div>
          <div className="border border-border">{rows.filter((r) => r.group_name === g).map(renderRow)}</div>
        </div>
      ))}


      <div className="mb-3 text-xs uppercase tracking-[0.18em] text-foreground/60">90-day plan copy</div>
      <div className="space-y-4">
        {content.map((c) => (
          <div key={c.section} className="border border-border p-4">
            <input
              className={`${inputCls} mb-2 font-medium`}
              value={c.heading}
              onChange={(e) => setContent((prev) => prev.map((x) => (x.section === c.section ? { ...x, heading: e.target.value } : x)))}
            />
            <textarea
              rows={4}
              className={inputCls}
              value={c.body}
              onChange={(e) => setContent((prev) => prev.map((x) => (x.section === c.section ? { ...x, body: e.target.value } : x)))}
            />
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button onClick={saveAll} disabled={busy} className="bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {busy ? "Saving…" : "Save settings"}
        </button>
        {status ? <span className="text-sm text-primary">{status}</span> : null}
      </div>
    </div>
  );
}
